import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Async Thunks
export const fetchYouTubeOAuthUrl = createAsyncThunk(
  'youtubeAds/fetchOAuthUrl',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/youtube/oauth/url');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to get OAuth URL');
    }
  }
);

export const fetchYouTubeConnectionStatus = createAsyncThunk(
  'youtubeAds/fetchConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/youtube/oauth/status');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch connection status');
    }
  }
);

export const disconnectYouTube = createAsyncThunk(
  'youtubeAds/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete('/marketing/youtube/oauth/disconnect');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to disconnect');
    }
  }
);

export const fetchYouTubeAdAccounts = createAsyncThunk(
  'youtubeAds/fetchAdAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/marketing/youtube/accounts');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch ad accounts');
    }
  }
);

export const fetchYouTubeCampaigns = createAsyncThunk(
  'youtubeAds/fetchCampaigns',
  async ({ advertiserId, offset = 0, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        offset,
        limit,
        ...(advertiserId && { advertiserId })
      });
      const response = await api.get(`/marketing/youtube/campaigns?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch campaigns');
    }
  }
);

export const createYouTubeCampaign = createAsyncThunk(
  'youtubeAds/createCampaign',
  async (campaignData, { rejectWithValue }) => {
    try {
      const response = await api.post('/marketing/youtube/campaigns', { campaignData });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create campaign');
    }
  }
);

export const updateYouTubeCampaign = createAsyncThunk(
  'youtubeAds/updateCampaign',
  async ({ campaignId, updates }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/marketing/youtube/campaigns/${campaignId}`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update campaign');
    }
  }
);

export const deleteYouTubeCampaign = createAsyncThunk(
  'youtubeAds/deleteCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/marketing/youtube/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete campaign');
    }
  }
);

export const syncYouTubeCampaignMetrics = createAsyncThunk(
  'youtubeAds/syncMetrics',
  async ({ campaignId, advertiserId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (advertiserId) params.append('advertiserId', advertiserId);
      const response = await api.post(`/marketing/youtube/campaigns/${campaignId}/sync?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to sync metrics');
    }
  }
);

// Initial State
const initialState = {
  connected: false,
  businessAccountId: null,
  businessAccountName: null,
  adAccounts: [],
  campaigns: [],
  selectedAccount: null,
  loading: false,
  error: null,
};

// Slice
const youtubeAdsSlice = createSlice({
  name: 'youtubeAds',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectAdAccount: (state, action) => {
      state.selectedAccount = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch OAuth URL
    builder.addCase(fetchYouTubeOAuthUrl.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchYouTubeOAuthUrl.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload.url) {
        window.location.href = action.payload.url;
      }
    });
    builder.addCase(fetchYouTubeOAuthUrl.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Connection Status
    builder.addCase(fetchYouTubeConnectionStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchYouTubeConnectionStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.connected = action.payload.connected || false;
      state.businessAccountId = action.payload.businessAccountId;
      state.businessAccountName = action.payload.businessAccountName;
      state.adAccounts = action.payload.adAccounts || [];
    });
    builder.addCase(fetchYouTubeConnectionStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Disconnect
    builder.addCase(disconnectYouTube.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(disconnectYouTube.fulfilled, (state) => {
      state.loading = false;
      state.connected = false;
      state.businessAccountId = null;
      state.businessAccountName = null;
      state.adAccounts = [];
      state.campaigns = [];
      state.selectedAccount = null;
    });
    builder.addCase(disconnectYouTube.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Ad Accounts
    builder.addCase(fetchYouTubeAdAccounts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchYouTubeAdAccounts.fulfilled, (state, action) => {
      state.loading = false;
      state.adAccounts = action.payload.adAccounts || [];
    });
    builder.addCase(fetchYouTubeAdAccounts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Fetch Campaigns
    builder.addCase(fetchYouTubeCampaigns.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchYouTubeCampaigns.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns = action.payload.campaigns || [];
    });
    builder.addCase(fetchYouTubeCampaigns.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Create Campaign
    builder.addCase(createYouTubeCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createYouTubeCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns.push(action.payload.campaign);
    });
    builder.addCase(createYouTubeCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Update Campaign
    builder.addCase(updateYouTubeCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateYouTubeCampaign.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.campaigns.findIndex(c => c._id === action.payload.campaign._id);
      if (index !== -1) {
        state.campaigns[index] = action.payload.campaign;
      }
    });
    builder.addCase(updateYouTubeCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Delete Campaign
    builder.addCase(deleteYouTubeCampaign.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteYouTubeCampaign.fulfilled, (state, action) => {
      state.loading = false;
      state.campaigns = state.campaigns.filter(c => c._id !== action.meta.arg.campaignId);
    });
    builder.addCase(deleteYouTubeCampaign.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    // Sync Metrics
    builder.addCase(syncYouTubeCampaignMetrics.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(syncYouTubeCampaignMetrics.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.campaigns.findIndex(c => c._id === action.payload.campaign._id);
      if (index !== -1) {
        state.campaigns[index] = action.payload.campaign;
      }
    });
    builder.addCase(syncYouTubeCampaignMetrics.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });
  },
});

export const { clearError, selectAdAccount } = youtubeAdsSlice.actions;
export default youtubeAdsSlice.reducer;
